/**
 * One line of a page, as the learner checks it (#376; BACKEND_DESIGN §20).
 *
 * The failure this component exists to prevent is a reader's silent "fix" of
 * the learner's own error being accepted as what the learner wrote - which
 * manufactures a right answer where there was a wrong one. So:
 *
 * - **Nothing a reader read is ever pre-selected.** A reading is a row the learner presses. "Neither of these…" opens
 *   "Fix the reading" (#660, `reading-editor.tsx`): the field starts as the first reading so the learner edits instead of
 *   typing maths from nothing, but pre-filling is not preselecting - nothing is sent until the learner saves, Save stays
 *   off until the text has changed, and what is sent is a `corrected` entry, the learner's own words.
 * - **A disagreement is resolved by the learner, never by a reader.** Two readings that differ are two rows, Option 1 and
 *   Option 2. The learner taps one (`chose_reading`, founder ruling 2026-10-31, #393: maths symbols are often impossible
 *   to type on a phone): the tap is the learner's own observation, shown as their choice. Those rows appear only where
 *   both readings exist and differ after the server's own comparison (NFC, whitespace collapsed, never accent folding);
 *   the server refuses a choice anywhere else, and refuses `confirmed_as_read` on a disagreement
 *   (`transcription_unresolved`).
 * - **Only `agreed` is certain**, and even then the learner can say "neither of these". A line a reader settled and the
 *   learner has not touched (#465) is used as read: drawn quietly, marked as the readers' and never the learner's, with
 *   no question, and decided from `resolved`, `effectiveSource` and the readings alone, so a source the server adds later
 *   shows the same generic "read from your page". Behind "Change" it stays correctable.
 * - **All reader and learner text is untrusted handwriting content.** Rendered
 *   as plain React children, never as HTML, markdown or maths, in a
 *   line (`dir="rtl"` with a Hebrew letter, else `dir="ltr"`) whose Hebrew phrases and other runs are each isolated
 *   (learners write Hebrew, but a reversed operand order would be a false
 *   accept; a fixed `dir` on the line alone was measured not to prevent it - see
 *   `untrusted-text.tsx`), with preserved whitespace (`.handwriting`) and
 *   invisible characters shown as markers. It is not trimmed or normalised
 *   here; the server decides what it keeps. Only the learner's text boxes are
 *   `dir="auto"`.
 * - **A line the learner did not write can be rejected** (#392): every line that
 *   has one candidate text offers "This line is not on my page" inside the editor, recorded as
 *   `not_written` - the learner's observation, never a deletion, and not the same as skipping a line. A rejected
 *   line stays on screen, struck through and labelled, with an Undo that simply
 *   records a new entry. A text-identical pair that split into two lines is
 *   asked about together, once, by `pair-review.tsx`.
 * - **Every line shows which question it is part of** (#406, `question-label.tsx`), and the learner can change it:
 *   confirming the page confirms the labels too.
 * - **Nothing here says whether the work is right.**
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { HomeworkTranscriptionResponse } from "@davinci/contracts";
import { ApiRequestError } from "../../api/client.js";
import { queryKeys } from "../../api/query-keys.js";
import { useApi } from "../../app/api-context.js";
import { FailureNotice } from "../../app/failure-notice.js";
import { useHomeworkLabels } from "../../i18n/homework-labels.js";
import { RegionCrop, type PageSize } from "./region-crop.js";
import { AnswerLockScope } from "./answer-lock.js";
import { ChoiceRow } from "./choice-row.js";
import { QuestionLabel } from "./question-label.js";
import { ReadingEditor } from "./reading-editor.js";
import { HandwrittenText, UntrustedText, lineDir } from "./untrusted-text.js";

/**
 * Mirrors the server's `normaliseForComparison` (apps/server/src/homework/reconcile.ts): NFC, runs of whitespace to
 * one space, trim. Nothing else - accents are never folded. The server's `singleCandidate` compares after it, so the
 * reader buttons must too.
 */
function textsDiffer(a: string, b: string): boolean {
  const norm = (text: string): string =>
    text.normalize("NFC").replace(/\s+/gu, " ").trim();
  return norm(a) !== norm(b);
}

type Region = HomeworkTranscriptionResponse["regions"][number];
type Reading = NonNullable<Region["primaryReading"]>;

function UncertainTokens({
  tokens,
}: {
  readonly tokens: readonly string[];
}): React.JSX.Element | null {
  const t = useHomeworkLabels();
  if (tokens.length === 0) return null;
  return (
    <div className="field-hint">
      <p>{t.readerUnsure}</p>
      <ul className="plain-list">
        {tokens.map((token, index) => (
          <li key={`${index}-${token}`}>
            <span className="handwriting" dir={lineDir(token)}>
              <UntrustedText text={token} />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReadingBlock({
  label,
  reading,
}: {
  readonly label: string;
  readonly reading: Reading | undefined;
}): React.JSX.Element {
  const t = useHomeworkLabels();
  return (
    <div className="reading">
      <p className="reading-label">{label}</p>
      {reading === undefined ? (
        <p className="field-hint">{t.readerNothing}</p>
      ) : (
        <>
          <HandwrittenText text={reading.text} />
          <UncertainTokens tokens={reading.uncertainTokens} />
        </>
      )}
    </div>
  );
}

export interface RegionReviewProps {
  readonly region: Region;
  readonly lineNumber: number;
  readonly pageId: string;
  readonly uploadId: string;
  /** The page image's size, for the crop of the learner's own handwriting (#393). */
  readonly pageSize: PageSize;
  /**
   * Told whenever this line starts or stops showing a text box the learner could
   * save. The page refuses to be confirmed while any line does: a box that is
   * open means the learner is in the middle of saying "this is not what I
   * wrote", and confirming then would accept the very text they are rejecting.
   */
  readonly onEditingChange: (regionId: string, editing: boolean) => void;
  /**
   * Presentation only (#619): the stepper keeps every line mounted - so a typed-but-unsaved box,
   * the editing flag the page reads, and a pending save all survive a step - and hides the ones
   * that are not the current step.
   */
  readonly hidden?: boolean;
  /** The learner is done with this line (#660): the server recorded what they said, or they left a question they came back to. The stepper moves on from there. */
  readonly onMoveOn?: (regionId: string) => void;
  /** The way to the previous step, drawn on the card's question row from the second step (#660). Absent: no link. */
  readonly onBack?: () => void;
  /**
   * Asks the card to open without the learner pressing its own control (#660): `ask` shows the question again on a line
   * already answered (Back); `editor` opens "Fix the reading" (Change on the quick look). A new `id` is a new request.
   */
  readonly openRequest?: {
    readonly mode: "ask" | "editor";
    readonly id: number;
  };
}

function RegionCard({
  region,
  lineNumber,
  pageId,
  uploadId,
  pageSize,
  onEditingChange,
  hidden,
  onMoveOn,
  onBack,
  openRequest,
}: RegionReviewProps): React.JSX.Element {
  const t = useHomeworkLabels();
  const api = useApi();
  const queryClient = useQueryClient();

  const {
    primaryReading: primary,
    secondReading: second,
    learnerEntry,
  } = region;
  // Two readings that differ have no single candidate, whatever the line is
  // called: the learner picks one or says neither, and nothing offers to confirm.
  const readersDiffer =
    primary !== undefined &&
    second !== undefined &&
    textsDiffer(primary.text, second.text);
  const typeOnly = region.agreement === "disagreed" || readersDiffer;
  const hasEntry = learnerEntry !== undefined;
  // The learner rejected this line's one candidate: settled, but it stands for no text (#392).
  const excluded = learnerEntry?.kind === "not_written" && region.resolved;
  // The learner said neither reading is right and left the line out (#658, #660): nothing is claimed about what was written.
  const skipped = learnerEntry?.kind === "skipped" && region.resolved;
  const firstReading = (primary ?? second)?.text ?? region.effectiveText ?? "";

  const [editing, setEditing] = useState(false);
  // "Fix the reading" shows once the learner presses "Neither of these…"; until then it is closed.
  const [boxOpen, setBoxOpen] = useState(false);
  const showSummary = hasEntry && !editing;

  // A box the learner could save: open, on a line that is not showing its
  // summary. The one exception is the open box of an unresolved line with two readings and
  // no entry yet - that line is unresolved, so `allResolved` already blocks the
  // page and nothing is being rejected.
  const boxCouldBeSaved =
    boxOpen && !showSummary && !(typeOnly && !hasEntry && !region.resolved);
  useEffect(() => {
    onEditingChange(region.regionId, boxCouldBeSaved);
  }, [onEditingChange, region.regionId, boxCouldBeSaved]);
  useEffect(
    () => () => onEditingChange(region.regionId, false),
    [onEditingChange, region.regionId],
  );

  const save = useMutation({
    mutationFn: (
      body:
        | { kind: "corrected"; text: string }
        | { kind: "confirmed_as_read" }
        | { kind: "not_written" }
        | { kind: "skipped" }
        | { kind: "chose_reading"; reader: "primary" | "second" },
    ) =>
      api.createHomeworkCorrection(
        pageId,
        region.regionId,
        crypto.randomUUID(),
        body,
      ),
    onSuccess: (updated) => {
      queryClient.setQueryData(
        queryKeys.homeworkTranscription(pageId),
        updated,
      );
      setEditing(false);
      setBoxOpen(false);
      onMoveOn?.(region.regionId);
    },
    // Success or refusal, what is on screen may no longer be what the server holds.
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.homeworkTranscription(pageId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.homeworkUpload(uploadId),
      });
    },
  });

  const requestId = openRequest?.id;
  const requestMode = openRequest?.mode;
  useEffect(() => {
    if (requestId === undefined) return;
    save.reset();
    setEditing(true);
    setBoxOpen(requestMode === "editor");
    // A request is its id: the effect runs once per request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const inputId = `region-${region.regionId}-text`;
  const failureSentence =
    save.error instanceof ApiRequestError
      ? t.reviewFailureSentence(save.error.code) || undefined
      : undefined;

  function startChange(): void {
    save.reset();
    setBoxOpen(false);
    setEditing(true);
  }

  function cancel(): void {
    save.reset();
    setBoxOpen(false);
    setEditing(false);
  }

  function openBox(): void {
    save.reset();
    setBoxOpen(true);
  }

  const askId = `region-${region.regionId}-ask`;
  const asking = !showSummary;
  const editorOpen = asking && boxOpen;
  // The question, in the board's words: two readings ask "what does this say?", one asks "is this what you wrote?".
  const ask = editorOpen
    ? t.fixTheReading
    : typeOnly
      ? t.didYouWrite
      : t.isThisWhatYouWrote;
  // The one reading the learner can confirm: a line a reader already settled and the learner has not touched offers no "Yes".
  const canConfirm = !region.resolved || hasEntry;
  const cancelLink = hasEntry ? (
    <button
      type="button"
      className="button-link"
      onClick={() => {
        cancel();
        onMoveOn?.(region.regionId);
      }}
    >
      {t.cancel}
    </button>
  ) : null;

  const editor = (
    <ReadingEditor
      inputId={inputId}
      initialText={
        learnerEntry?.kind === "corrected" && learnerEntry.text !== undefined
          ? learnerEntry.text
          : firstReading
      }
      pending={save.isPending}
      onSave={(text) => save.mutate({ kind: "corrected", text })}
      onCancel={cancel}
      // The server refuses `not_written` where the two readings differ, so it is offered only where there is one text.
      {...(typeOnly || excluded
        ? {}
        : { onNotOnPage: () => save.mutate({ kind: "not_written" }) })}
      {...(skipped ? {} : { onSkip: () => save.mutate({ kind: "skipped" }) })}
    />
  );

  return (
    <li className="region region-line" hidden={hidden}>
      <AnswerLockScope active={hidden !== true}>
      <h3 className="visually-hidden">{t.lineN(lineNumber)}</h3>
      <div className="region-picture">
        <RegionCrop
          pageId={pageId}
          bbox={region.bbox}
          approximate={region.bboxApproximate}
          lineNumber={lineNumber}
          text={region.effectiveText ?? firstReading}
          {...pageSize}
        />
      </div>
      {/* Which question the line is part of (#406): every line but one the learner said is not on their page, or skipped. */}
      {excluded || skipped ? null : (
        <QuestionLabel
          region={region}
          pageId={pageId}
          uploadId={uploadId}
          onEditingChange={onEditingChange}
        />
      )}

      <div
        className="region-answer"
        role={asking ? "group" : undefined}
        aria-labelledby={asking ? askId : undefined}
      >
        {asking ? (
          <div className="region-ask-row">
            <p className="region-ask" id={askId}>
              {ask}
            </p>
            {onBack !== undefined && !editorOpen ? (
              <button type="button" className="button-link" onClick={onBack}>
                {t.previousLine}
              </button>
            ) : null}
          </div>
        ) : null}
        {showSummary ? (
          <>
            {excluded ? (
              <>
                <p className="region-state">
                  <strong>{t.notOnMyPageLabel}.</strong>{" "}
                  {t.notOnMyPageExplanation}
                </p>
                {/* The rejected reading stays visible, struck through: nothing is hidden or deleted. */}
                <div className="region-excluded">
                  <HandwrittenText text={learnerEntry.text ?? ""} />
                </div>
              </>
            ) : skipped ? (
              <p className="region-state">
                <strong>{t.skippedState}</strong>
              </p>
            ) : (
              <>
                <p className="region-state">
                  {learnerEntry.kind === "corrected"
                    ? t.youCorrected
                    : learnerEntry.kind === "confirmed_as_read"
                      ? t.youConfirmed
                      : learnerEntry.kind === "chose_reading"
                        ? learnerEntry.chosenReader === "primary"
                          ? t.youChoseOption(1)
                          : learnerEntry.chosenReader === "second"
                            ? t.youChoseOption(2)
                            : t.youChoseAReading
                        : // A rejection that no longer stands (the server failed it closed): never shown as a confirmation.
                          `${t.notOnMyPageLabel}.`}
                </p>
                <HandwrittenText
                  text={region.effectiveText ?? learnerEntry.text ?? ""}
                />
              </>
            )}
            <div className="region-actions">
              <button
                type="button"
                className="button-link"
                onClick={startChange}
              >
                {excluded ? t.undo : t.changeThis}
              </button>
            </div>
          </>
        ) : (
          <>
            {excluded ? (
              <p className="region-state">
                <strong>{t.notOnMyPageLabel}.</strong>{" "}
                {t.notOnMyPageExplanation}
              </p>
            ) : skipped ? (
              <p className="region-state">
                <strong>{t.skippedState}</strong>
              </p>
            ) : hasEntry ? (
              <>
                {/* What stands while the learner changes it: never hidden by the edit. */}
                <p className="region-state">{t.saved}</p>
                <HandwrittenText
                  text={region.effectiveText ?? learnerEntry?.text ?? ""}
                />
              </>
            ) : null}
            {editorOpen ? (
              editor
            ) : (
              <>
                <p className="region-state">
                  {readersDiffer && region.agreement !== "disagreed"
                    ? t.agreement("disagreed")
                    : t.agreement(region.agreement)}
                </p>

                {typeOnly ? (
                  readersDiffer ? (
                    <ReadingChoices
                      primary={primary}
                      second={second}
                      disabled={save.isPending}
                      onChoose={(reader) =>
                        save.mutate({ kind: "chose_reading", reader })
                      }
                    />
                  ) : (
                    <div className="readings">
                      <ReadingBlock label={t.optionN(1)} reading={primary} />
                      <ReadingBlock label={t.optionN(2)} reading={second} />
                    </div>
                  )
                ) : (
                  <>
                    <HandwrittenText
                      text={
                        region.agreement === "agreed"
                          ? (region.agreedText ?? primary?.text ?? "")
                          : ((primary ?? second)?.text ?? "")
                      }
                    />
                    <UncertainTokens
                      tokens={[
                        ...new Set([
                          ...(primary?.uncertainTokens ?? []),
                          ...(second?.uncertainTokens ?? []),
                        ]),
                      ]}
                    />
                  </>
                )}
                <div className="choice-rows">
                  {/* A reading is pressed, never pre-selected: "Yes" confirms the one reading as read. */}
                  {!typeOnly && canConfirm ? (
                    <ChoiceRow
                      disabled={save.isPending}
                      onClick={() => save.mutate({ kind: "confirmed_as_read" })}
                    >
                      {t.yesWhatIWrote}
                    </ChoiceRow>
                  ) : null}
                  <ChoiceRow disabled={save.isPending} onClick={openBox}>
                    {t.neitherOfThese}
                  </ChoiceRow>
                </div>
                {cancelLink}
              </>
            )}
          </>
        )}
      </div>

      {save.isError ? (
        failureSentence !== undefined ? (
          <div className="notice notice-error" role="alert">
            <p>{failureSentence}</p>
          </div>
        ) : (
          <FailureNotice error={save.error} />
        )
      ) : null}
      </AnswerLockScope>
    </li>
  );
}

/**
 * Both readings, when they differ: one row each, with the option's small label above the reading's text. Pressing a row
 * is the learner's choice of that reader, the same call "Option N is right" made (`chose_reading`); neither is ever
 * pre-selected. Any token a reader was unsure about stays on screen under the rows.
 */
function ReadingChoices({
  primary,
  second,
  disabled,
  onChoose,
}: {
  readonly primary: Reading;
  readonly second: Reading;
  readonly disabled: boolean;
  readonly onChoose: (reader: "primary" | "second") => void;
}): React.JSX.Element {
  const t = useHomeworkLabels();
  const rows: readonly (readonly [1 | 2, Reading, "primary" | "second"])[] = [
    [1, primary, "primary"],
    [2, second, "second"],
  ];
  return (
    <>
      <div className="choice-rows">
        {rows.map(([slot, reading, reader]) => (
          <ChoiceRow
            key={reader}
            disabled={disabled}
            onClick={() => onChoose(reader)}
          >
            <span className="reading-label">{t.optionN(slot)}</span>
            <HandwrittenText text={reading.text} />
          </ChoiceRow>
        ))}
      </div>
      <UncertainTokens
        tokens={[
          ...new Set([...primary.uncertainTokens, ...second.uncertainTokens]),
        ]}
      />
    </>
  );
}

/**
 * A line a reader settled and the learner has not touched (#465, #660): used as read, correctable. Decided from what the
 * server says about the line - it is resolved, no learner entry stands, and its source is not one of the learner's - and
 * never from a list of sources, so a source the server adds later is drawn the same generic way ("read from your page").
 * Two readers that agreed keep their own card, which says so.
 */
function isReadLine(region: Region): boolean {
  return (
    region.resolved &&
    region.learnerEntry === undefined &&
    region.agreement !== "agreed" &&
    region.effectiveSource !== undefined &&
    !region.effectiveSource.startsWith("learner_")
  );
}

function ReadLine({
  region,
  lineNumber,
  pageId,
  uploadId,
  pageSize,
  onEditingChange,
  hidden,
  onMoveOn,
  openRequest,
}: RegionReviewProps): React.JSX.Element {
  const t = useHomeworkLabels();
  const api = useApi();
  const queryClient = useQueryClient();
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    onEditingChange(region.regionId, changing);
  }, [onEditingChange, region.regionId, changing]);
  useEffect(
    () => () => onEditingChange(region.regionId, false),
    [onEditingChange, region.regionId],
  );

  const save = useMutation({
    mutationFn: (
      body:
        | { kind: "corrected"; text: string }
        | { kind: "not_written" }
        | { kind: "skipped" }
        | { kind: "chose_reading"; reader: "primary" | "second" },
    ) =>
      api.createHomeworkCorrection(
        pageId,
        region.regionId,
        crypto.randomUUID(),
        body,
      ),
    onSuccess: (updated) => {
      queryClient.setQueryData(
        queryKeys.homeworkTranscription(pageId),
        updated,
      );
      setChanging(false);
      onMoveOn?.(region.regionId);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.homeworkTranscription(pageId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.homeworkUpload(uploadId),
      });
    },
  });

  const requestId = openRequest?.id;
  const requestMode = openRequest?.mode;
  useEffect(() => {
    if (requestId === undefined || requestMode !== "editor") return;
    save.reset();
    setChanging(true);
    // A request is its id: the effect runs once per request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const inputId = `region-${region.regionId}-text`;
  const failureSentence =
    save.error instanceof ApiRequestError
      ? t.reviewFailureSentence(save.error.code) || undefined
      : undefined;
  const readersDiffer =
    region.primaryReading !== undefined &&
    region.secondReading !== undefined &&
    textsDiffer(region.primaryReading.text, region.secondReading.text);
  // Two readings that differ: the learner may pick one, and the server refuses `not_written` here.
  const twoReadings = readersDiffer || region.agreement === "disagreed";
  const firstReading =
    (region.primaryReading ?? region.secondReading)?.text ??
    region.effectiveText ??
    "";

  function cancel(): void {
    save.reset();
    setChanging(false);
  }

  return (
    <li className="region region-line" hidden={hidden}>
      <AnswerLockScope active={hidden !== true}>
      <h3 className="visually-hidden">{t.lineN(lineNumber)}</h3>
      <div className="region-picture">
        <RegionCrop
          pageId={pageId}
          bbox={region.bbox}
          approximate={region.bboxApproximate}
          lineNumber={lineNumber}
          text={region.effectiveText ?? firstReading}
          {...pageSize}
        />
      </div>
      <QuestionLabel
        region={region}
        pageId={pageId}
        uploadId={uploadId}
        onEditingChange={onEditingChange}
      />
      <div className="region-answer">
        {changing ? (
          <div className="region-ask-row">
            <p className="region-ask">{t.fixTheReading}</p>
          </div>
        ) : null}
        <p className="region-state">{t.readFromPage}</p>
        <HandwrittenText text={region.effectiveText ?? ""} />

        {changing ? (
          <>
            {twoReadings ? (
              readersDiffer &&
              region.primaryReading !== undefined &&
              region.secondReading !== undefined ? (
                <ReadingChoices
                  primary={region.primaryReading}
                  second={region.secondReading}
                  disabled={save.isPending}
                  onChoose={(reader) =>
                    save.mutate({ kind: "chose_reading", reader })
                  }
                />
              ) : (
                <div className="readings">
                  <ReadingBlock
                    label={t.optionN(1)}
                    reading={region.primaryReading}
                  />
                  <ReadingBlock
                    label={t.optionN(2)}
                    reading={region.secondReading}
                  />
                </div>
              )
            ) : null}
            <ReadingEditor
              inputId={inputId}
              initialText={firstReading}
              pending={save.isPending}
              onSave={(text) => save.mutate({ kind: "corrected", text })}
              onCancel={cancel}
              {...(twoReadings
                ? {}
                : { onNotOnPage: () => save.mutate({ kind: "not_written" }) })}
              onSkip={() => save.mutate({ kind: "skipped" })}
            />
          </>
        ) : (
          <div className="region-actions">
            <button
              type="button"
              className="button-link"
              onClick={() => {
                save.reset();
                setChanging(true);
              }}
            >
              {t.change}
            </button>
          </div>
        )}
      </div>

      {save.isError ? (
        failureSentence !== undefined ? (
          <div className="notice notice-error" role="alert">
            <p>{failureSentence}</p>
          </div>
        ) : (
          <FailureNotice error={save.error} />
        )
      ) : null}
      </AnswerLockScope>
    </li>
  );
}

/**
 * A line the check does not use (#465): drawn quietly, with the readers' text. It may still be a question header the parser
 * did not read as one, so it carries the same label control as any line (#548); once labelled it is asked, and returns to review.
 */
function NotCheckedLine({
  region,
  lineNumber,
  pageId,
  uploadId,
  onEditingChange,
  hidden,
}: RegionReviewProps): React.JSX.Element {
  const t = useHomeworkLabels();
  const { primaryReading: primary, secondReading: second } = region;
  const differ =
    primary !== undefined &&
    second !== undefined &&
    textsDiffer(primary.text, second.text);
  return (
    <li className="region region-not-checked" hidden={hidden}>
      <h3>{t.lineN(lineNumber)}</h3>
      <p className="region-state">{t.notUsedInCheck}</p>
      <QuestionLabel
        region={region}
        pageId={pageId}
        uploadId={uploadId}
        onEditingChange={onEditingChange}
      />
      {differ ? (
        <div className="readings">
          <ReadingBlock label={t.optionN(1)} reading={primary} />
          <ReadingBlock label={t.optionN(2)} reading={second} />
        </div>
      ) : (
        <HandwrittenText text={(primary ?? second)?.text ?? ""} />
      )}
    </li>
  );
}

export function RegionReview(props: RegionReviewProps): React.JSX.Element {
  if (props.region.readNotChecked === true)
    return <NotCheckedLine {...props} />;
  return isReadLine(props.region) ? (
    <ReadLine {...props} />
  ) : (
    <RegionCard {...props} />
  );
}
